import { useEffect, type PropsWithChildren } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import { LaborActiveSessionProvider } from "@/components/LaborActiveSessionProvider";
import { apiFetch, integratorMeQueryRetry, integratorMeQueryRetryDelay } from "@/lib/api";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { meQueryKey, type MeResponse } from "@/lib/me-query";
import { useHubCacheStream } from "@/lib/useHubCacheStream";

/**
 * Warms `/api/me` as soon as auth resolves so hub RBAC (`integratorSubappAccess`) is usually ready before labor
 * and other gated surfaces mount. Backend identity is cached; SSE `hub.identity.*` tells this tab to refetch
 * when LISTEN sees user_setting / roles / module access change.
 */
export function IntegratorMePrefetch({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const { loading, session } = useAuth();
  const userId = session?.user?.id ?? null;

  useEffect(() => {
    if (loading || userId == null || userId === "") return;
    void queryClient.prefetchQuery({
      queryKey: meQueryKey(userId),
      queryFn: () => apiFetch<MeResponse>("/api/me"),
      retry: integratorMeQueryRetry,
      retryDelay: integratorMeQueryRetryDelay,
    });
  }, [loading, queryClient, userId]);

  useHubCacheStream({
    channels:
      userId != null && userId !== ""
        ? [HUB_CACHE_CHANNEL.hubIdentityMe(userId), HUB_CACHE_CHANNEL.hubIdentityModules]
        : [],
    enabled: userId != null && userId !== "",
    connectionKey: userId,
    onEvent: (event) => {
      if (event.phase === "connected") {
        return;
      }
      if (userId == null || userId === "") {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: meQueryKey(userId) });
    },
  });

  if (!session?.user) {
    return children;
  }

  return <LaborActiveSessionProvider>{children}</LaborActiveSessionProvider>;
}
